import { useMemo, useState } from 'react';
import { RankedBars } from '../../charts/RankedBars';
import { Card, Segmented, Stat, StatusChip } from '../../components/data';
import { Avatar, ErrorBanner, Loading } from '../../components/ui';
import { num, relativeDays, snakeLabel } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import type { AdminPeopleData, AnnouncementRow, PeopleBoardPerson } from './adminTypes';
import { RegisterPanel, formatIsoShort, type RegisterSpec } from './adminUi';

const ANNOUNCEMENT_CATEGORY = ['general', 'milestone', 'policy', 'event', 'kudos'] as const;

const HORIZONS = [
  { id: '30', label: '30 days' },
  { id: '60', label: '60 days' },
  { id: '90', label: '90 days' },
];

/**
 * People and internal communications.
 *
 * Reads `core.Employee` — the directory this app owns — NOT a staff list of its
 * own. The app this came from carried thirty rows of name, title, department,
 * manager and hire date, which was a second employee table in an estate that has
 * one; see (1) in migration 048. Work anniversaries now roll off
 * `core.Employee.StartDate`, the date payroll already agrees with.
 *
 * One column had nowhere to go: date of birth. It was added to
 * `coo.EmployeeProfile`, which already holds the personal half of a person's
 * record, and it is frequently null — so the board reports how many people it does
 * NOT know about beside how many it does. Three names means something very
 * different if twenty-seven have no date recorded.
 *
 * The API sends only the NEXT occurrence of a birthday, never the original date.
 * The day and month are what the board needs; the year is the person's business,
 * and sending it to every browser that opens this page would publish their age.
 */
export function AdminPeople({ canWrite, onChanged }: { canWrite: boolean; onChanged: () => void }) {
  const [horizon, setHorizon] = useState(60);
  const board = useResource<AdminPeopleData>(`/admin/analytics/people?horizon=${horizon}`);
  const employees = useResource<Employee[]>('/employees?status=active');

  const spec = useMemo<RegisterSpec<AnnouncementRow>>(
    () => ({
      resource: 'announcements',
      title: 'The noticeboard',
      singular: 'Announcement',
      emptyTitle: 'Nothing posted yet',
      emptyBody:
        'Company announcements — a policy change, an event, a new starter, a thank you. Pinned posts stay at the top of the board above.',
      searchHint: 'Title, body or author',
      idOf: (row) => row.AnnouncementId,
      labelOf: (row) => row.Title,
      filters: [{ param: 'category', label: 'Category', options: ANNOUNCEMENT_CATEGORY }],
      columns: [
        {
          key: 'Title',
          label: 'Announcement',
          render: (row) => (
            <div>
              <div className="admin-cell-title">
                {row.Pinned && <span className="admin-pin" aria-label="Pinned">📌 </span>}
                {row.Title}
              </div>
              <div className="xs muted">
                {snakeLabel(row.Category)} · {row.Audience}
                {row.AuthorName ? ` · ${row.AuthorName}` : ''}
              </div>
            </div>
          ),
        },
        {
          key: 'PublishDate',
          label: 'Published',
          render: (row) => formatIsoShort(row.PublishDate),
        },
        {
          key: 'Body',
          label: 'Body',
          /* Truncated in the register list — the board above shows it in full. */
          render: (row) => (
            <span className="sub">
              {row.Body.length > 120 ? `${row.Body.slice(0, 119)}…` : row.Body}
            </span>
          ),
        },
      ],
      fields: [
        { name: 'title', label: 'Title', kind: 'text', wide: true },
        { name: 'body', label: 'Body', kind: 'textarea', wide: true },
        { name: 'category', label: 'Category', kind: 'select', options: ANNOUNCEMENT_CATEGORY },
        { name: 'audience', label: 'Audience', kind: 'text' },
        { name: 'publishDate', label: 'Publish date', kind: 'date' },
        {
          name: 'authorEmployeeId',
          label: 'Author',
          kind: 'select',
          optional: true,
          placeholder: 'Unattributed',
          choices: (employees.data ?? []).map((e) => ({
            value: String(e.EmployeeId),
            label: e.FullName,
          })),
        },
        { name: 'pinned', label: 'Pin to the top of the board', kind: 'checkbox' },
      ],
      blank: {
        title: '',
        body: '',
        category: 'general',
        audience: 'All Staff',
        publishDate: '',
        authorEmployeeId: '',
        pinned: false,
      },
      toForm: (row) => ({
        title: row.Title,
        body: row.Body,
        category: row.Category,
        audience: row.Audience,
        publishDate: row.PublishDate?.slice(0, 10) ?? '',
        authorEmployeeId: row.AuthorEmployeeId ?? '',
        pinned: row.Pinned,
      }),
    }),
    [employees.data],
  );

  const reload = () => {
    board.reload();
    onChanged();
  };

  const d = board.data;
  const s = d?.summary;

  return (
    <div className="admin-stack">
      {board.error && <ErrorBanner message={board.error} />}
      {!d && board.loading && <Loading label="Loading the board" />}

      {d && s && (
        <>
          <div className="row-between wrap">
            <p className="sub">
              Read from the shared employee directory, not a staff list this module keeps. Anybody on
              leave is included — they still have a birthday.
            </p>
            <Segmented
              label="Horizon"
              value={String(horizon)}
              options={HORIZONS}
              onChange={(next) => setHorizon(Number(next))}
            />
          </div>

          <div className="grid grid-4">
            <Stat
              label="Headcount"
              value={s.headcount}
              foot={
                s.onLeave > 0
                  ? `${s.onLeave} on leave · ${s.departments} departments`
                  : `across ${s.departments} departments`
              }
            />
            <Stat
              label="Average tenure"
              value={s.avgTenureYears === null ? '—' : `${num(s.avgTenureYears, 1)} yrs`}
              foot={
                s.startDateUnknown > 0
                  ? `${s.startDateUnknown} have no start date recorded`
                  : 'across everybody employed'
              }
            />
            <Stat
              label="Birthdays, 30d"
              value={s.birthdays30}
              /* The honest counterpart: a small number here means something
                 different if most people have no date on file. */
              foot={
                s.birthDateUnknown > 0
                  ? `${s.birthDateUnknown} have no date of birth on file`
                  : 'coming up in the next month'
              }
            />
            <Stat
              label="Anniversaries, 30d"
              value={s.anniversaries30}
              foot={
                s.milestones > 0
                  ? `${s.milestones} ${s.milestones === 1 ? 'milestone' : 'milestones'} in the window`
                  : 'coming up in the next month'
              }
            />
          </div>

          <div className="admin-split">
            <Card
              title="Birthdays"
              subtitle={`The next ${horizon} days. Shown without the year.`}
            >
              {d.birthdays.length === 0 ? (
                <p className="sub">
                  {s.birthDateUnknown === s.headcount
                    ? 'No date of birth is recorded for anybody. They are set on a person’s profile under People, alongside the rest of the personal half of their record.'
                    : `No birthdays in the next ${horizon} days.`}
                </p>
              ) : (
                <ul className="admin-people-list">
                  {d.birthdays.map((p) => (
                    <PersonRow key={p.employeeId} person={p} />
                  ))}
                </ul>
              )}
            </Card>

            <Card
              title="Work anniversaries"
              subtitle={`The next ${horizon} days, from the start date on the directory record.`}
            >
              {d.anniversaries.length === 0 ? (
                <p className="sub">No anniversaries in the next {horizon} days.</p>
              ) : (
                <ul className="admin-people-list">
                  {d.anniversaries.map((p) => (
                    <PersonRow
                      key={p.employeeId}
                      person={p}
                      badge={
                        /* Milestones are called out because they are the ones
                           worth doing something about — 1, 3, 5, 10 and up. */
                        p.milestone ? (
                          <StatusChip band="good">{p.years} years</StatusChip>
                        ) : (
                          <span className="xs muted">{p.years} years</span>
                        )
                      }
                    />
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <div className="admin-split">
            <Card title="Headcount by department" subtitle="Everybody employed, by where they sit.">
              <RankedBars
                rows={d.byDepartment.map((dept) => ({
                  label: dept.departmentName,
                  value: dept.headcount,
                }))}
                format={(v) => num(v)}
                labelWidth={160}
                emptyMessage="Nobody in the directory yet."
              />
            </Card>

            <Card title="Tenure" subtitle="How long people have been here.">
              <RankedBars
                rows={d.tenureBands.map((band) => ({ label: band.label, value: band.headcount }))}
                format={(v) => num(v)}
                labelWidth={132}
              />
              {s.startDateUnknown > 0 && (
                <p className="sub mt-2">
                  {num(s.startDateUnknown)}{' '}
                  {s.startDateUnknown === 1 ? 'person has' : 'people have'} no start date on their
                  record and appear in none of these bands.
                </p>
              )}
            </Card>
          </div>

          <Card
            title="The noticeboard"
            subtitle="Pinned first, then newest. The twenty-five most recent."
          >
            {d.announcements.length === 0 ? (
              <p className="sub">Nothing has been posted yet.</p>
            ) : (
              <ul className="admin-board">
                {d.announcements.map((a) => (
                  <li key={a.announcementId} className={a.pinned ? 'pinned' : undefined}>
                    <div className="head">
                      <h3>
                        {a.pinned && (
                          <span className="admin-pin" aria-label="Pinned">
                            📌{' '}
                          </span>
                        )}
                        {a.title}
                      </h3>
                      <span className="xs muted">
                        {snakeLabel(a.category)} · {formatIsoShort(a.publishDate)}
                        {a.authorName ? ` · ${a.authorName}` : ''}
                      </span>
                    </div>
                    <p>{a.body}</p>
                    {a.audience !== 'All Staff' && (
                      <span className="xs muted">Audience: {a.audience}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      <RegisterPanel spec={spec} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}

/** One person on the birthday or anniversary board. */
function PersonRow({
  person,
  badge,
}: {
  person: PeopleBoardPerson;
  badge?: React.ReactNode;
}) {
  return (
    <li>
      <Avatar name={person.fullName} />
      <div className="who">
        <div className="admin-cell-title">{person.fullName}</div>
        <div className="xs muted">
          {[person.roleTitle, person.departmentName, person.location].filter(Boolean).join(' · ') ||
            'no role recorded'}
        </div>
      </div>
      <div className="when">
        <div>{formatIsoShort(person.nextDate)}</div>
        <div className="xs muted">{relativeDays(person.daysAway)}</div>
      </div>
      {badge && <div className="badge-slot">{badge}</div>}
    </li>
  );
}

